import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// AJ Code Labs Logo Component (Brand Guidelines)
const LogoAJ = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="96" height="96" rx="28" fill="#FAFAFA" />
    <g transform="translate(22.5,22.2) scale(0.55)">
      <path d="M8 84 L31 16 L54 84" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M19 61 H43" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M92 16 V63 C92 76 82 85 69 85 C61 85 55 82 51 77" stroke="#FF4D2D" strokeWidth="11" strokeLinecap="square" />
    </g>
  </svg>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.about'), href: '#about' },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const scrollToSection = (href, isRoute = false) => {
    setIsMobileMenuOpen(false);
    
    if (isRoute) {
      navigate(href);
      return;
    }
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A0A0B] shadow-lg' : 'bg-[#0A0A0B]'
      }`}
      data-testid="navbar"
    >
      <div className="container-custom flex items-center justify-between py-4">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="navbar-logo"
        >
          <motion.div whileHover={{ scale: 1.02 }}>
            <LogoAJ size={44} />
          </motion.div>
          <span className="font-semibold text-lg text-[#FAFAFA] tracking-tight">
            AJ Code Labs
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <motion.button
              key={link.name}
              onClick={() => scrollToSection(link.href, link.isRoute)}
              className="text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-300 font-medium text-sm"
              whileHover={{ y: -2 }}
              data-testid={`nav-link-${link.href.replace('#', '').replace('/', '')}`}
            >
              {link.name}
            </motion.button>
          ))}
          
          {/* Language Toggle */}
          <motion.button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-3 py-2 border border-[#27272A] rounded-full text-[#A1A1AA] hover:text-[#FF4D2D] hover:border-[#FF4D2D]/30 transition-colors duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-mono uppercase">{language === 'es' ? 'EN' : 'ES'}</span>
          </motion.button>

          <motion.button
            onClick={() => scrollToSection('#contact')}
            className="bg-[#E03A1C] hover:bg-[#FF4D2D] text-[#FAFAFA] px-6 py-2.5 rounded-full font-semibold text-sm transition-colors duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="nav-cta-button"
          >
            {t('nav.cta')}
          </motion.button>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Mobile Language Toggle */}
          <motion.button
            onClick={toggleLanguage}
            className="flex items-center gap-1 px-2 py-1 border border-[#27272A] rounded-full text-[#A1A1AA]"
            whileTap={{ scale: 0.95 }}
            data-testid="mobile-language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="text-xs font-mono uppercase">{language === 'es' ? 'EN' : 'ES'}</span>
          </motion.button>
          
          <button
            className="text-[#FAFAFA] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-[#0A0A0B] border-t border-[#27272A] px-6 py-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href, link.isRoute)}
                className="text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-300 font-medium text-left py-2"
              >
                {link.name}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#E03A1C] hover:bg-[#FF4D2D] text-[#FAFAFA] px-6 py-3 rounded-full font-semibold text-sm mt-2 transition-colors duration-300"
            >
              {t('nav.cta')}
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
